import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Truck, CheckCircle2, Clock, Download, RotateCcw, Search, ExternalLink, MapPin, ShoppingBag } from "lucide-react";
import { useUserData } from "../../hooks/useUserData";

const statusIcon = { "In Production": Clock, Shipped: Truck, Delivered: CheckCircle2, Cancelled: Package };
const statusBg = { amber: "bg-amber-100 text-amber-700", blue: "bg-blue-100 text-blue-700", emerald: "bg-emerald-100 text-emerald-700", zinc: "bg-zinc-200 text-zinc-600" };

const Orders = () => {
  const { orders } = useUserData();
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);

  const filtered = useMemo(() => {
    let list = orders;
    if (filter !== "all") list = list.filter((o) => o.status.toLowerCase().replace(" ", "-") === filter);
    if (q) list = list.filter((o) => `${o.id} ${o.product}`.toLowerCase().includes(q.toLowerCase()));
    return list;
  }, [orders, filter, q]);

  if (orders.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-zinc-200 p-6">
        <h1 className="text-2xl font-bold text-zinc-900">My Orders</h1>
        <p className="text-sm text-zinc-500 mt-1">Track, reorder and download invoices for everything you've printed with us.</p>
        <div className="py-16 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-rose-50 text-rose-500 flex items-center justify-center mb-4">
            <Package className="w-7 h-7" />
          </div>
          <div className="font-semibold text-zinc-900 text-lg">No orders yet</div>
          <p className="text-sm text-zinc-500 mt-1 max-w-sm mx-auto">Once you place your first order, you'll see status, tracking and reorder options here.</p>
          <Link to="/shop" className="inline-flex items-center gap-2 mt-6 bg-zinc-900 text-white px-6 py-3 rounded-lg text-sm font-bold hover:bg-rose-500 transition-colors">
            <ShoppingBag className="w-4 h-4" /> Shop products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-white rounded-2xl border border-zinc-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">My Orders</h1>
            <p className="text-sm text-zinc-500 mt-1">Track, reorder and download invoices for everything you've printed with us.</p>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order ID or product" className="pl-9 pr-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none focus:border-rose-400 w-full sm:w-72" />
          </div>
        </div>

        <div className="mt-5 flex items-center gap-2 overflow-x-auto pb-1">
          {[
            { id: "all", label: "All" },
            { id: "in-production", label: "In production" },
            { id: "shipped", label: "Shipped" },
            { id: "delivered", label: "Delivered" },
            { id: "cancelled", label: "Cancelled" },
          ].map((t) => (
            <button key={t.id} onClick={() => setFilter(t.id)} className={`text-xs font-semibold px-3.5 py-2 rounded-full whitespace-nowrap transition-colors ${filter === t.id ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>{t.label}</button>
          ))}
        </div>
      </div>

      <div className="space-y-4 mt-5">
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-zinc-200 py-16 text-center">
            <Package className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
            <div className="font-semibold text-zinc-900">No orders match that filter</div>
            <p className="text-sm text-zinc-500 mt-1">Try a different status or clear your search.</p>
          </div>
        ) : filtered.map((o) => {
          const Icon = statusIcon[o.status] || Package;
          const isOpen = expanded === o.id;
          return (
            <div key={o.id} className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
              <button onClick={() => setExpanded(isOpen ? null : o.id)} className="w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-zinc-50 transition-colors">
                <img src={o.image} alt="" className="w-16 h-16 rounded-lg object-cover bg-zinc-50 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-bold text-zinc-900">{o.product}</span>
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${statusBg[o.statusColor]}`}>
                      <Icon className="w-3 h-3 inline mr-1 -mt-0.5" />{o.status}
                    </span>
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">Order <span className="font-semibold text-zinc-700">{o.id}</span> · {o.qty} units · placed {o.date}</div>
                </div>
                <div className="text-right hidden sm:block">
                  <div className="font-bold text-zinc-900">${o.total.toFixed(2)}</div>
                  <div className="text-[11px] text-zinc-500">ETA: {o.eta}</div>
                </div>
              </button>
              {isOpen && (
                <div className="px-5 pb-5 pt-2 border-t border-zinc-100 grid lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <div className="text-xs uppercase tracking-wider text-zinc-500 font-bold mb-2">Configuration</div>
                    <dl className="text-sm divide-y divide-zinc-100">
                      {Object.entries(o.config || {}).map(([k, v]) => (
                        <div key={k} className="py-2 flex justify-between gap-4">
                          <dt className="capitalize text-zinc-500">{k}</dt>
                          <dd className="font-semibold text-zinc-800">{v}</dd>
                        </div>
                      ))}
                      <div className="py-2 flex justify-between gap-4"><dt className="text-zinc-500">Quantity</dt><dd className="font-semibold text-zinc-800">{o.qty}</dd></div>
                    </dl>
                    {o.tracking && (
                      <div className="mt-4 bg-blue-50 border border-blue-100 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-blue-700"><Truck className="w-3.5 h-3.5" /> Tracking</div>
                        <div className="flex items-center justify-between gap-3 mt-1.5">
                          <code className="text-sm font-mono text-zinc-800">{o.tracking}</code>
                          <button onClick={() => alert("Live tracking opens once the backend is wired.")} className="text-xs font-semibold text-blue-700 hover:underline inline-flex items-center gap-1">Track <ExternalLink className="w-3 h-3" /></button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="bg-zinc-50 rounded-lg p-4 text-sm">
                      <div className="flex justify-between text-zinc-600"><span>Subtotal</span><span>${o.subtotal.toFixed(2)}</span></div>
                      <div className="flex justify-between text-zinc-600 mt-1"><span>Shipping</span><span>{o.shipping === 0 ? "Free" : `$${o.shipping.toFixed(2)}`}</span></div>
                      <div className="flex justify-between text-zinc-600 mt-1"><span>Tax</span><span>${o.tax.toFixed(2)}</span></div>
                      <div className="border-t border-zinc-200 mt-3 pt-3 flex justify-between font-bold text-zinc-900"><span>Total</span><span>${o.total.toFixed(2)}</span></div>
                    </div>
                    <div className="mt-3 space-y-2">
                      <Link to={`/product/${o.productSlug}`} className="w-full inline-flex items-center justify-center gap-2 bg-zinc-900 text-white py-2.5 rounded-lg text-xs font-bold hover:bg-rose-500 transition-colors">
                        <RotateCcw className="w-3.5 h-3.5" /> Reorder
                      </Link>
                      <button onClick={() => alert("Invoice PDF download will work once the backend is live.")} className="w-full inline-flex items-center justify-center gap-2 border border-zinc-200 text-zinc-800 py-2.5 rounded-lg text-xs font-bold hover:border-zinc-400 transition-colors">
                        <Download className="w-3.5 h-3.5" /> Download invoice
                      </button>
                    </div>
                    <div className="mt-4 text-xs text-zinc-500 flex items-start gap-2">
                      <MapPin className="w-3.5 h-3.5 text-rose-500 shrink-0 mt-0.5" /> Pickup at our 3291 W Sunrise Blvd studio is always free.
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Orders;
